import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import CaseTypeSelect from '../components/CaseTypeSelect';
import { caseTypeLabel } from '../constants/caseTypes';
import HelpLink from '../components/help/HelpLink';

export default function Cases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newCase, setNewCase] = useState({ case_name: '', case_number: '', case_type: 'other', notes: '' });
  const [creating, setCreating] = useState(false);

  const limit = 20;

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/cases', { skip: page * limit, limit, search: search || undefined });
      setCases(data.cases);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const created = await api.post('/cases', newCase);
      setShowCreate(false);
      setNewCase({ case_name: '', case_number: '', case_type: 'other', notes: '' });
      navigate(`/case/${created.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    setPage(0);
    // fetchCases will re-run via useEffect when page/search changes
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="cases-page">
      <div className="page-header">
        <h1>Cases</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New Case
        </button>
      </div>

      {/* Search */}
      <form className="search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search cases…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="input"
        />
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Case list */}
      {loading ? (
        <div className="loading">Loading cases…</div>
      ) : cases.length === 0 ? (
        search.trim() ? (
          <div className="empty-state">
            <p>No cases match "{search}".</p>
          </div>
        ) : (
          <FirstRunHero user={user} onCreate={() => setShowCreate(true)} />
        )
      ) : (
        <>
          <CaseGrid cases={cases.filter(c => !c.access)} navigate={navigate} />
          {cases.some(c => c.access === 'consultant') && (
            <>
              <h2 className="case-group-title" style={{ marginTop: 24, fontSize: 16 }}>
                Consulting
                <span className="settings-hint" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12, color: 'var(--text-muted, #6c757d)' }}>
                  Cases another organization has shared with you
                </span>
              </h2>
              <CaseGrid cases={cases.filter(c => c.access === 'consultant')} navigate={navigate} />
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                ← Prev
              </button>
              <span className="pagination-info">Page {page + 1} of {totalPages}</span>
              <button className="btn btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* First-run hero rendered above; create modal below */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Create New Case</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Case Name *</label>
                <input
                  className="input"
                  required
                  value={newCase.case_name}
                  onChange={e => setNewCase(s => ({ ...s, case_name: e.target.value }))}
                  placeholder="e.g. State v. Smith"
                />
              </div>
              <div className="form-group">
                <label>Case Number</label>
                <input
                  className="input"
                  value={newCase.case_number}
                  onChange={e => setNewCase(s => ({ ...s, case_number: e.target.value }))}
                  placeholder="e.g. 2026-CR-00123"
                />
              </div>
              <div className="form-group">
                <label>Case Type *</label>
                <CaseTypeSelect
                  value={newCase.case_type}
                  onChange={v => setNewCase(s => ({ ...s, case_type: v }))}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="input"
                  rows={3}
                  value={newCase.notes}
                  onChange={e => setNewCase(s => ({ ...s, notes: e.target.value }))}
                  placeholder="Optional case notes…"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : 'Create Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CaseGrid({ cases, navigate }) {
  if (cases.length === 0) return null;
  return (
    <div className="case-grid">
      {cases.map(c => (
        <div key={c.id} className="case-card" onClick={() => navigate(`/case/${c.id}`)}>
          <div className="case-card-header">
            <h3>{c.name}</h3>
            <span className="badge badge-type">{caseTypeLabel(c.case_type)}</span>
          </div>
          {c.case_number && <p className="case-number" title={`Case number: ${c.case_number}`}>#{c.case_number}</p>}
          {c.access === 'consultant' && (
            <p className="case-consulting" style={{ fontSize: 12, color: 'var(--text-muted, #6c757d)', margin: '4px 0 0' }}
               title="Shared with you as an outside consultant">
              For {c.organization_name || 'another organization'}
              {c.grant_expires_at && ` · until ${new Date(c.grant_expires_at).toLocaleDateString()}`}
            </p>
          )}
          <div className="case-card-footer">
            <span>{c.document_count} docs</span>
            {c.pending_count > 0 && (
              <span className="badge badge-pending">{c.pending_count} pending</span>
            )}
            <span className="case-date" title={`Created ${new Date(c.created_at).toLocaleDateString()}`}>
              Created {new Date(c.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function FirstRunHero({ user, onCreate }) {
  const firstName = (user?.name || user?.email || '').split(/[ @]/)[0];
  return (
    <div className="first-run-hero">
      <div className="first-run-greeting">
        <h2>Welcome to ChargeStack Arc{firstName ? `, ${firstName}` : ''}.</h2>
        <p className="first-run-tagline">Ingest. Organize. Understand.</p>
      </div>
      <p className="first-run-blurb">
        Drop discovery into a case and Arc characterizes, summarizes, and helps
        you find what's there — and what isn't. Start by creating a case, then
        upload PDFs, audio, video, or images.
      </p>
      <div className="first-run-cta">
        <button className="btn btn-primary btn-lg" onClick={onCreate}>
          + Create your first case
        </button>
      </div>
      <ol className="first-run-steps">
        <li><strong>Create a case</strong> — give it a name and case number.</li>
        <li><strong>Upload discovery</strong> — drag-and-drop or pick a folder.</li>
        <li><strong>Ask Arc</strong> — once docs are processed, search, summarize, or generate a checklist.</li>
      </ol>
      <p className="first-run-learn-more">
        Want the longer tour first?{' '}
        <HelpLink slug="your-first-case" label="Your first case">Read the walkthrough →</HelpLink>
      </p>
    </div>
  );
}
